// equip-page-readers.ts — Janus lane I2/I3 S2b (2026-09-28): binds the pure equip rules (janus-engine/inventory/equip-rules.ts) to THIS
// page's caches. Every definition comes from the page (the caster's / encounter's catalog, the object cache, the full-nest finder); a
// feat or ability the page does not hold answers null, and the equip is refused with words rather than guessed (the legacy endpoints are
// deleted). N6f: a feat with its own condition gate (if_* / requires_*) is JUDGED here (featConditionsMet), no longer refused outright.
import type { EquipRuleReaders, Row } from '../janus-engine/inventory/equip-rules';
// N6f (2026-10-02): the compile's own check_ability_conditions() twin (read-only import — N4d's file) judges a CONDITIONED feat on the page.
import { createAbilityConditionEvaluator, type AbilityConditionSource } from '../janus-engine/compile/ability-conditions';
import type { ItemFieldRow } from '../janus-engine/compile/item-field-reader';
import { C1_CONTRACT, compileCreatureEquippedCounts } from '../janus-engine/compile/possessor-compile';
import { answerConditionContext } from '../janus-engine/compile/condition-context';
import { surfaceField } from '../janus-engine/compile/from-base-encounter-media';
import { heldLayerlessDefinition } from '../janus-engine/compile/held-definition';

const U = (v: unknown): string => String(v ?? '').trim().toUpperCase();
const asRow = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
const nonEmpty = (v: unknown): boolean => !(v === undefined || v === null || v === false || v === 0 || v === '' || v === '0' || (Array.isArray(v) && v.length === 0));
const list = (v: unknown): unknown[] => Array.isArray(v) ? v : asRow(v) ? Object.values(asRow(v)!) : [];
/** passive_ability_active_ability_ids(): an entry whose `passive_conditions_active` stamp is PRESENT and falsy (false / null / '' / 0 /
 *  '0') is inactive; an absent stamp is active (Codex review S2b rv3 #3). */
const stampActive = (e: unknown): boolean => { const r = asRow(e); return !r || !('passive_conditions_active' in r) || nonEmpty(r.passive_conditions_active); };
/** Complete definitions may omit an empty layer bucket; identity-only shells still need warming. */
const heldDefinition = (value: unknown): Row | null => {
  const row = asRow(value);
  return heldLayerlessDefinition(row) ? row : null;
};
/** Inline payloads already carry their layers; unlike catalog rows, they need no identity bucket. */
const inlineDefinition = (value: unknown): Row | null => {
  const row = asRow(value);
  return asRow(row?.item_layer_types) ? row : heldDefinition(row);
};
/** The encounter scalar (its `encounters` bucket, else the row itself). */
const encounterScalar = (enc: Row | null, key: string): string => {
  for (const bucket of [asRow(enc?.encounters), enc]) {
    const v = bucket?.[key];
    const s = String(Array.isArray(v) ? v[0] ?? '' : v ?? '').trim();
    if (s) return s;
  }
  return '';
};

type Win = {
  ftLayerValue?: (row: unknown, key: string) => unknown;
  ftLayerEntries?: (row: unknown, key: string) => unknown;
  ftFindFullNestedItem?: (id: string) => Row | null;
  ftResolveCachedItemForRender?: (id: string) => Row | null;
  FateTide?: { objectCache?: Map<string, unknown> };
};
const w = (): Win => window as unknown as Win;

/** The UUID a layer-list entry names (a bare id, a shell, an {item_arr} row, an ability-bucket row). */
export function entryId(entry: unknown): string {
  if (typeof entry === 'string') return U(entry);
  const r = asRow(entry);
  if (!r) return '';
  const nested = asRow(r.item_arr);
  return U(asRow(r.items)?.item_id ?? r.ability_id ?? r.item_id ?? asRow(nested?.items)?.item_id ?? '');
}

export function createEquipPageReaders(caster: Row, encounter: Row | null): EquipRuleReaders & { definition(id: string): Row | null; fat(row: Row): Row; missingDefinitions(): string[] } {
  const value = (row: Row | null | undefined, key: string): unknown => row ? w().ftLayerValue?.(row, key) ?? null : null;
  const entries = (row: Row | null | undefined, key: string): unknown[] => row ? list(w().ftLayerEntries?.(row, key)) : [];
  /** A complete held definition, including a lean layerless status, or null. */
  const definition = (id: string): Row | null => {
    const ID = U(id);
    if (!UUID.test(ID)) return null;
    for (const root of [caster, encounter]) {
      const cat = asRow(root?.catalog);
      const hit = heldDefinition(cat?.[ID] ?? cat?.[ID.toLowerCase()] ?? cat?.[id]);
      if (hit) return hit;
    }
    const cached = heldDefinition(w().FateTide?.objectCache?.get(ID) ?? w().FateTide?.objectCache?.get(ID.toLowerCase()));
    if (cached) return cached;
    try { return heldDefinition(w().ftFindFullNestedItem?.(ID) ?? null); } catch { return null; }
  };
  /** A thin inventory row with its template's definition overlaid (the page's own renderer first). */
  const fat = (row: Row): Row => {
    if (asRow(row.item_layer_types)) return row;
    const iid = U(asRow(row.inventory)?.inventory_id);
    try { const rendered = iid ? w().ftResolveCachedItemForRender?.(iid) : null; if (rendered && asRow(rendered.item_layer_types)) return { ...rendered, inventory: row.inventory }; } catch { /* fall through */ }
    const def = definition(String(asRow(row.inventory)?.inventory_item_id ?? asRow(row.items)?.item_id ?? ''));
    return def ? { ...def, ...row, items: { ...(asRow(def.items) ?? {}), ...(asRow(row.items) ?? {}) }, item_layer_types: def.item_layer_types } : row;
  };
  const creature = (): Row => asRow(asRow(caster.computed)?.creature) ?? {};
  /** An inventory row anywhere in the caster's carried tree (the evaluator's _ft_resolve_inventory_instance_full source). */
  const rowById = (id: string): Row | null => {
    const ID = U(id);
    let hit: Row | null = null;
    const walk = (nodes: unknown, depth: number): void => {
      if (hit || depth > 12) return;
      for (const n of list(nodes)) {
        const r = asRow(n); if (!r) continue;
        if (U(asRow(r.inventory)?.inventory_id) === ID) { hit = r; return; }
        walk(r.inventory_in_container, depth + 1);
      }
    };
    walk(caster.inventory, 0);
    return hit;
  };
  /**
   * N6f (2026-10-02): the contextual answers check_ability_conditions() reads (AbilityConditionDependencies.conditionSource). N4e
   * (2026-10-02): answered from the page through the SHARED scene answers (compile/condition-context.ts — the C1 compile reads the same
   * module): the encounter's light-type set, the effective light at the creature, outdoors, direct sunlight, a controlled summon within
   * range, the set-enchantment precondition, a median charge; plus a draw-bound instance's sheathed state here. A context the page does not
   * hold THROWS (never guessed): the feat is then unknown and the equip is refused with words.
   */
  const conditionSource = (kind: AbilityConditionSource, args: readonly unknown[]): unknown => {
    if (kind === 'draw-sheathed') {
      // ft_draw_bound_inventory_is_sheathed (the C1 compile's own answer): the bound instance is held and NOT equipped.
      const bound = Array.isArray(args[1]) ? args[1][0] : args[1];
      const inst = rowById(String(bound ?? ''));
      return !!inst && !nonEmpty(asRow(inst.inventory)?.equipped);
    }
    // N4e (2026-10-02): every other context — the encounter's light set, the light AT the creature, outdoors, direct sunlight, a controlled
    // summon in range, the set-enchantment precondition, a median charge — comes from the SHARED scene answers the C1 compile reads too
    // (janus-engine/compile/condition-context.ts): this page supplies its encounter + campaign row. A context the page does not hold
    // (another encounter, an uncached campaign, a charges row) throws → the feat is unknown and the equip is refused with words.
    const campId = encounter && encounterScalar(encounter, 'encounter_master_parent_type').toLowerCase() === 'campaign' ? U(encounterScalar(encounter, 'encounter_master_parent_id')) : '';
    const cache = (window as unknown as { FateTide?: { campaignCache?: unknown } }).FateTide?.campaignCache;
    const cachedCampaign = cache instanceof Map && campId ? cache.get(campId) ?? cache.get(campId.toLowerCase()) : null;
    const camp = cachedCampaign ?? asRow(encounter?.campaign_arr) ?? asRow(caster.campaign_arr)
      ?? asRow(asRow(caster.encounter_context)?.campaign_arr);
    return answerConditionContext(kind, [asRow(args[0]) ?? caster, ...args.slice(1)], {
      encounter, campaign: camp, value: (row, key) => value(row, key), entries: (row, key) => entries(row, key),
      abilityTypes: C1_CONTRACT.abilityTypes, definition: id => definition(id),
    });
  };
  let evaluator: ReturnType<typeof createAbilityConditionEvaluator> | null = null;
  const conditions = (): ReturnType<typeof createAbilityConditionEvaluator> => evaluator ??= createAbilityConditionEvaluator({
    catalogRows: C1_CONTRACT.catalogRows, abilityTypes: C1_CONTRACT.abilityTypes,
    aggregationStrategy: (name: string) => C1_CONTRACT.catalogRows[name]?.aggregation_strategy ?? 'host_wins',
    // AbilityConditionDependencies' contract: an UNAVAILABLE reference throws (null would read as a known miss — a silently ungated carrier).
    resolveCarrierRef: (id: string) => { const d = definition(id); if (!d) throw new Error(`def-missing:${id}`); return d as ItemFieldRow; },
    resolveInventoryInstance: (id: string) => { const r = rowById(id); return r ? fat(r) as ItemFieldRow : null; },
    queryTemplateDefinition: (id: string) => definition(id) as ItemFieldRow | null,
    equipmentSlots: C1_CONTRACT.equipmentSlots, vitalityStatusIds: C1_CONTRACT.vitalityStatusIds, skillEntries: C1_CONTRACT.skillEntries,
    conditionSource,
  });
  /**
   * N6f: does a feat's OWN condition gate hold for this caster? check_ability_conditions($feat, $caster, false, false) — the rule
   * ft_creature_holds_passive_ability() / find_caster_layer_provider_arrs() apply before a feat grants anything. true / false = judged;
   * null = the page cannot judge it (a context it does not model, a definition / instance it does not hold — the evaluator throws rather
   * than guess). Codex N6f review P2: the evaluator's OWN preflight (ability_has_runtime_conditions — root-level layers, default-provider
   * carriers, PHP's presence rule) decides whether there is a gate at all. A carrier the page does not hold THROWS (resolveCarrierRef — the
   * evaluator's contract: an unavailable reference is never a "known miss") and the feat is unknown (Codex N6f round 2: that carrier may
   * carry the gate); missingDefinitions() names those carriers, so the equip seam's cold-page warm-up fetches them and re-plans.
   */
  const featConditionsMet = (def: Row): boolean | null => {
    let gated: boolean;
    try { gated = conditions().hasRuntimeConditions(def as ItemFieldRow); } catch { return null; }
    if (!gated) return true;
    try { return conditions().check(def as ItemFieldRow, caster as ItemFieldRow, false, false); } catch { return null; }
  };
  /** Feat ids the creature holds (its feat layer + the compile's skill-ability feats). */
  const featIds = (): string[] => [...entries(caster, 'feat'), ...list(asRow(creature().skill_ability)?.feat)].map(entryId).filter(Boolean);
  /** Each held feat with the definition PHP judges (Codex N6f review P2): an entry's own inline row (a {source, item_arr} wrapper's
   *  item_arr, or an entry that is itself a full row — ft_resolve_layer_entry passes those through), else the page's definition by id. */
  const featDefs = (): Array<{ id: string; def: Row | null }> => [...entries(caster, 'feat'), ...list(asRow(creature().skill_ability)?.feat)].map(e => {
    const inline = asRow(asRow(e)?.item_arr) ?? asRow(e);
    const full = heldDefinition(inline);
    const id = entryId(e);
    return { id, def: full ?? (id ? definition(id) : null) };
  }).filter(f => f.id !== '');
  /** Every ability-bucket entry whose passive conditions have not switched it off. */
  const BUCKETS = ['passive_ability', 'full_ability', 'ability', 'quick_ability', 'attack_ability', 'free_ability', 'reaction_ability', 'opportunity_attack_ability'];
  const activeAbilities = (): unknown[] => BUCKETS.flatMap(b => entries(caster, b)).filter(stampActive);

  const holds = (_c: Row, id: string): boolean | null => {
    const ID = U(id);
    if (featIds().includes(ID)) return true;
    if (entries(caster, 'passive_ability').filter(stampActive).map(entryId).includes(ID)) return true;
    // ft_creature_holds_passive_ability descends feat grants (they never reach the passive bucket) — only through a feat whose own
    // conditions hold (N6f: judged by featConditionsMet; one the page cannot judge is unknown — review S2b rv3 #2).
    let unknown = false;
    for (const { def } of featDefs()) {
      if (!def) { unknown = true; continue; }
      if (!BUCKETS.some(b => entries(def, b).map(entryId).includes(ID))) continue;
      const met = featConditionsMet(def);
      if (met === null) { unknown = true; continue; }
      if (!met) continue;
      return true;
    }
    return unknown ? null : false;
  };
  const provides = (_c: Row, layer: string): boolean | null => {
    if (nonEmpty(value(caster, layer))) return true;
    let unknown = false;
    const judge = (entry: unknown): void => {
      const row = inlineDefinition(asRow(entry)?.item_arr) ?? inlineDefinition(entry) ?? definition(entryId(entry));
      if (!row) { unknown = true; return; }
      if (nonEmpty(value(row, layer))) throw TRUE;
    };
    const TRUE = Symbol('provides');
    try {
      for (const e of activeAbilities()) judge(e);
      for (const { def } of featDefs()) {
        if (!def) { unknown = true; continue; }
        // find_caster_layer_provider_arrs: a feat whose own if_* / requires_* gate fails grants NOTHING (N6f: judged by featConditionsMet).
        const met = featConditionsMet(def);
        if (met === null) { // a feat whose own conditions the page cannot judge: its layer / grants decide nothing (review S2b rv3 #2)
          if (nonEmpty(value(def, layer)) || BUCKETS.some(b => entries(def, b).length)) unknown = true;
          continue;
        }
        if (!met) continue;
        if (nonEmpty(value(def, layer))) throw TRUE;
        for (const b of BUCKETS) for (const g of entries(def, b)) judge(g);
      }
      // resolve_adv_dis_layer_sources: an EQUIPPED item, and a held status, can carry it too.
      const walk = (nodes: unknown, depth: number): void => {
        if (depth > 6) return;
        for (const n of list(nodes)) {
          const r = asRow(n); if (!r) continue;
          if (nonEmpty(asRow(r.inventory)?.equipped) && Number(asRow(r.inventory)?.inventory_private) !== 2 && nonEmpty(value(fat(r), layer))) throw TRUE;
          walk(r.inventory_in_container, depth + 1);
        }
      };
      walk(caster.inventory, 0);
      for (const s of list(asRow(caster.statuses)?.target)) {
        if (Number(surfaceField(asRow(s), 'status_deleted') ?? 0) !== 0 || !stampActive(s)) continue;
        const def = inlineDefinition(asRow(s)?.status_arr) ?? definition(String(surfaceField(asRow(s), 'status_item_id') ?? ''));
        if (!def) { unknown = true; continue; }
        const met = featConditionsMet(def);
        if (met === null) { if (nonEmpty(value(def, layer))) unknown = true; continue; }
        if (!met) continue;
        if (nonEmpty(value(def, layer))) throw TRUE;
      }
    } catch (e) {
      if (e === TRUE) return true;
      throw e;
    }
    return unknown ? null : false;
  };
  return {
    value, entries, definition, fat,
    itemType: row => String(asRow(row?.items)?.item_type ?? (row ? asRow(fat(row).items)?.item_type : '') ?? ''),
    inventoryField: (row, key) => asRow(row?.inventory)?.[key],
    sizeId: c => U(asRow(asRow(asRow(asRow(c.computed)?.creature)?.size)?.breakdown)?.final_size_id ?? value(c, 'size') ?? ''),
    body: c => {
      const total = Number(value(c, 'body') ?? asRow(asRow(asRow(creature().essences)?.body))?.total ?? 0);
      return Number.isFinite(total) ? total : 0;
    },
    count: (c, key) => Number(compileCreatureEquippedCounts(c, { definition })?.[key] ?? 0),
    holds, provides,
    itemPassives: row => [
      ...entries(row, 'passive_ability').map(entryId),
      ...list(asRow(asRow(asRow(row.computed)?.enchantment)?.abilities)?.passive_ability).filter(stampActive).map(entryId),
    ].filter(Boolean),
    missingDefinitions: () => {
      // N6b (R-088 / R-089 / R-101): every definition holds() / provides() / fat() would read and the page lacks — the feats, their
      // grants, the ability entries without an inline row, the carried rows' templates and the held statuses. The equip seam fetches
      // these once and re-plans, so an `unknown` verdict means a genuinely undecidable (conditioned) feat, not a cold page.
      const out = new Set<string>();
      const need = (id: string): void => { const ID = U(id); if (UUID.test(ID) && !definition(ID)) out.add(ID); };
      for (const f of featIds()) {
        need(f);
        const def = definition(f);
        if (def) for (const b of BUCKETS) for (const g of entries(def, b)) if (!inlineDefinition(asRow(g)?.item_arr) && !inlineDefinition(g)) need(entryId(g));
      }
      // N6f: the default-provider carriers a held feat names (a weapon classification, a creature type …) — the condition preflight walks
      // them and treats one the page lacks as unknown.
      for (const { def } of featDefs()) {
        const layers = asRow(def?.item_layer_types) ?? {};
        for (const [name, row] of Object.entries(C1_CONTRACT.catalogRows)) {
          if (row.default_provider !== 1 || !(name in layers)) continue;
          const slot = layers[name];
          for (const ref of typeof slot === 'string' ? [slot] : list(slot)) if (typeof ref === 'string') for (const id of ref.split(/\s+/)) need(id);
        }
      }
      for (const e of activeAbilities()) if (!inlineDefinition(asRow(e)?.item_arr) && !inlineDefinition(e)) need(entryId(e));
      const walk = (nodes: unknown, depth: number): void => {
        if (depth > 6) return;
        for (const n of list(nodes)) {
          const r = asRow(n); if (!r) continue;
          if (!asRow(r.item_layer_types)) need(String(asRow(r.inventory)?.inventory_item_id ?? asRow(r.items)?.item_id ?? ''));
          walk(r.inventory_in_container, depth + 1);
        }
      };
      walk(caster.inventory, 0);
      for (const s of list(asRow(caster.statuses)?.target)) if (!inlineDefinition(asRow(s)?.status_arr)) need(String(surfaceField(asRow(s), 'status_item_id') ?? ''));
      return [...out];
    },
  };
}

/** ft_creature_matches_active_combat(), exactly: a TAGGED combat = the actor's combat_id equals the encounter's active_combat_id (whatever
 *  in_combat says); a wholly UNTAGGED legacy combat (in_combat 1, no active id, no resident tagged) counts every untagged resident. null =
 *  the page does not hold the actor's encounter (the caller hands back — review S2b rv3 #5). The server reads the same facts from SQL. */
export function actorInCombat(caster: Row, encounter: Row | null): boolean | null {
  const encounterId = U(asRow(caster.encounter_creatures)?.encounter_id);
  if (!UUID.test(encounterId)) return false; // off-scene: no combat
  const enc = asRow(encounter?.encounters) ?? encounter;
  if (!enc) return null;
  const norm = (v: unknown): string => (UUID.test(U(v)) ? U(v) : '');
  const active = norm(enc.active_combat_id), member = norm(asRow(caster.encounter_creatures)?.combat_id);
  if (active !== '') return member !== '' && member === active;
  if (Number(enc.in_combat) !== 1 || member !== '') return false;
  const residents = list(encounter?.encounter_creatures_arr);
  if (residents.some(r => norm(asRow(asRow(r)?.encounter_creatures)?.combat_id) !== '')) return false;
  const me = U(asRow(caster.encounter_creatures)?.encounter_creature_id);
  return residents.some(r => U(asRow(asRow(r)?.encounter_creatures)?.encounter_creature_id) === me);
}
